import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { User, AuthTokens } from '@/types'

interface AuthState {
  user: User | null
  tokens: AuthTokens | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null

  // Actions
  login: (user: User, tokens: AuthTokens) => void
  logout: () => void
  setUser: (user: User) => void
  setTokens: (tokens: AuthTokens) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  clearError: () => void
  refreshToken: () => Promise<boolean>
  fetchProfile: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      tokens: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: (user, tokens) => {
        // Sync to localStorage for the API interceptor
        localStorage.setItem('accessToken', tokens.accessToken)
        localStorage.setItem('refreshToken', tokens.refreshToken)
        set({
          user,
          tokens,
          isAuthenticated: true,
          error: null,
        })
      },

      logout: () => {
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        set({
          user: null,
          tokens: null,
          isAuthenticated: false,
          error: null,
        })
      },

      setUser: (user) => set({ user }),

      setTokens: (tokens) => {
        localStorage.setItem('accessToken', tokens.accessToken)
        localStorage.setItem('refreshToken', tokens.refreshToken)
        set({ tokens })
      },

      setLoading: (loading) => set({ isLoading: loading }),

      setError: (error) => set({ error }),

      clearError: () => set({ error: null }),

      refreshToken: async (): Promise<boolean> => {
        const { tokens } = get()
        if (!tokens?.refreshToken) return false

        try {
          const { apiClient } = await import('@/services/api')
          const response = await apiClient.post<{ tokens: AuthTokens }>(
            '/auth/refresh',
            { refreshToken: tokens.refreshToken }
          )
          const newTokens = response.tokens
          localStorage.setItem('accessToken', newTokens.accessToken)
          localStorage.setItem('refreshToken', newTokens.refreshToken)
          set({ tokens: newTokens })
          return true
        } catch {
          get().logout()
          return false
        }
      },

      fetchProfile: async () => {
        try {
          const { apiClient } = await import('@/services/api')
          const response = await apiClient.get<any>('/auth/me')
          // apiClient.get already unwraps envelope → { user: {...} }
          const raw = (response.user ?? response.data?.user) as Record<string, unknown>
          // Normalize: backend returns flat roleName/roleId/level/permissions
          // but User type expects nested role: { id, name, level, permissions }
          const user: User = {
            ...(raw as unknown as User),
            role: (raw.role as User['role']) ?? {
              id: (raw.roleId as string) ?? '',
              name: (raw.roleName as string) ?? 'VIEWER',
              level: (raw.level as number) ?? 5,
              permissions: (raw.permissions as Record<string, string[]>) ?? {},
              canDeletePed: (raw.canDeletePed as boolean) ?? false,
              canOrderSmm: (raw.canOrderSmm as boolean) ?? false,
            },
          }
          set({ user, isAuthenticated: true })
        } catch {
          set({ user: null, isAuthenticated: false })
        }
      },
    }),
    {
      name: 'equipe-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        tokens: state.tokens,
        isAuthenticated: state.isAuthenticated,
      }),
      // On rehydrate, sync tokens back to localStorage keys used by API interceptor
      onRehydrateStorage: () => (state) => {
        if (state?.tokens) {
          localStorage.setItem('accessToken', state.tokens.accessToken)
          localStorage.setItem('refreshToken', state.tokens.refreshToken)
        }
      },
    }
  )
)

// Typed selectors
export const selectUser = (state: AuthState) => state.user
export const selectIsAuthenticated = (state: AuthState) => state.isAuthenticated
export const selectAccessToken = (state: AuthState) => state.tokens?.accessToken
